import { titleKey } from './trending.js';
import { normalizeText, cleanName, qualityOf } from './utils.js';

/* Une el catálogo del proveedor principal con el de los proveedores añadidos. Módulo puro.

   - El principal no cambia: sus títulos conservan su identificador, así que Mi lista, el progreso
     y las descargas que ya existen siguen valiendo.
   - Un título de otro proveedor que ya está en el catálogo no se repite: se apunta como
     alternativa (`alt`) del que ya hay, y sirve de respaldo si la reproducción falla.
   - Un título que solo tiene otro proveedor se añade con identificador «proveedor~id» y
     `source` = ese proveedor. */

export const SEP = '~';

export function compositeId(source, id) {
    return source + SEP + id;
}

/** 'abc~123' → { source: 'abc', rawId: '123' }; '123' → { source: '', rawId: '123' } */
export function splitId(id) {
    const s = String(id);
    const i = s.indexOf(SEP);
    return i === -1 ? { source: '', rawId: s } : { source: s.slice(0, i), rawId: s.slice(i + 1) };
}

function channelKey(ch) {
    const name = normalizeText(cleanName(ch.name));
    if (!name) return '';
    // Mismo canal y misma calidad: «La 1 HD» no se une con «La 1 FHD».
    const epgId = String(ch.epgId || '').trim().toLowerCase();
    return epgId ? 'e:' + epgId + '|' + (qualityOf(ch.name) || '') : 'n:' + name;
}

/** Índice de lo que ya hay, para encontrar el mismo título en otro proveedor. */
function buildIndex(kind, items) {
    const byTmdb = new Map();
    const byKey = new Map();
    items.forEach((item, i) => {
        if (kind === 'live') {
            const k = channelKey(item);
            if (k && !byKey.has(k)) byKey.set(k, [i]);
            return;
        }
        if (item.tmdb && !byTmdb.has(String(item.tmdb))) byTmdb.set(String(item.tmdb), i);
        const k = titleKey(item.name);
        if (!k) return;
        const list = byKey.get(k);
        if (list) list.push(i); else byKey.set(k, [i]);
    });
    return { byTmdb, byKey };
}

function findMatch(kind, index, items, item) {
    if (kind === 'live') {
        const hit = index.byKey.get(channelKey(item));
        return hit ? hit[0] : -1;
    }
    if (item.tmdb && index.byTmdb.has(String(item.tmdb))) return index.byTmdb.get(String(item.tmdb));
    const list = index.byKey.get(titleKey(item.name));
    if (!list) return -1;
    const year = Number(item.year) || 0;
    // Mismo título con otro año es otra obra (un remake). Si alguno no trae año, vale el título.
    let loose = -1;
    for (let n = 0; n < list.length; n++) {
        const y = Number(items[list[n]].year) || 0;
        if (year && y && year === y) return list[n];
        if ((!year || !y) && loose === -1) loose = list[n];
    }
    return loose;
}

/**
 * primary: { live, vod, series, categories }
 * extras:  [{ id, data: { live, vod, series, categories } }] en el orden en que deben sumarse.
 * Devuelve { live, vod, series, categories, stats } con stats[id][kind] = { added, merged }.
 */
export function mergeCatalogs(primary, extras) {
    const out = { categories: {}, stats: {} };
    const kinds = ['live', 'vod', 'series'];
    (extras || []).forEach((x) => { out.stats[x.id] = {}; });
    kinds.forEach((kind) => {
        const items = (primary[kind] || []).slice();
        const cats = ((primary.categories && primary.categories[kind]) || []).slice();
        const catByName = new Map();
        cats.forEach((c) => { const k = normalizeText(c.name); if (k && !catByName.has(k)) catByName.set(k, c.id); });
        const index = buildIndex(kind, items);
        const copied = new Set();

        (extras || []).forEach((x) => {
            const data = x.data || {};
            const stat = { added: 0, merged: 0 };
            out.stats[x.id][kind] = stat;
            // Categorías: la que se llama igual que una existente se une a ella.
            const catMap = new Map();
            ((data.categories && data.categories[kind]) || []).forEach((c) => {
                const k = normalizeText(c.name);
                if (k && catByName.has(k)) {
                    catMap.set(String(c.id), catByName.get(k));
                    return;
                }
                const id = compositeId(x.id, c.id);
                catMap.set(String(c.id), id);
                if (k) catByName.set(k, id);
                cats.push(Object.assign({}, c, { id, source: x.id }));
            });
            (data[kind] || []).forEach((item) => {
                const at = findMatch(kind, index, items, item);
                const alt = { source: x.id, id: String(item.id), name: item.name, ext: item.ext || '', url: item.url || '' };
                if (item.part) alt.part = item.part;
                if (at !== -1) {
                    // Se trabaja sobre una copia: el catálogo original (el de la caché) no se toca.
                    if (!copied.has(at)) {
                        items[at] = Object.assign({}, items[at]);
                        copied.add(at);
                    }
                    if (!items[at].alt) items[at].alt = [];
                    // Una alternativa por proveedor: la primera coincidencia.
                    if (!items[at].alt.some((a) => a.source === x.id)) {
                        items[at].alt.push(alt);
                        stat.merged++;
                    }
                    return;
                }
                const added = Object.assign({}, item, {
                    id: compositeId(x.id, item.id),
                    rawId: String(item.id),
                    source: x.id,
                    categoryId: catMap.get(String(item.categoryId)) || compositeId(x.id, item.categoryId || '')
                });
                items.push(added);
                copied.add(items.length - 1);
                stat.added++;
                // Lo recién añadido también puede coincidir con un tercer proveedor.
                const i = items.length - 1;
                if (kind === 'live') {
                    const k = channelKey(added);
                    if (k && !index.byKey.has(k)) index.byKey.set(k, [i]);
                } else {
                    if (added.tmdb && !index.byTmdb.has(String(added.tmdb))) index.byTmdb.set(String(added.tmdb), i);
                    const k = titleKey(added.name);
                    if (k) {
                        const list = index.byKey.get(k);
                        if (list) list.push(i); else index.byKey.set(k, [i]);
                    }
                }
            });
        });
        out[kind] = items;
        out.categories[kind] = cats;
    });
    return out;
}
